import { useState } from 'react';
import { Platform } from 'react-native';
import { apiService } from '../services/api';
import { Contact } from '../components/ContactsList';
import { parseInternationalPhoneNumber } from '../utils/phoneNumber';
import { getLastCommunicationChannel } from '../utils/lastCommunicationChannel';

const isMaskedPhoneValue = (value?: string | null) => !!value && value.includes('*');
const PANE_IDENTIFIER_CHANNELS = new Set(['whatsapp', 'phone', 'facebook-messenger', 'instagram', 'telegram', 'x-twitter']);

export type ContactIdentifierOption = {
  key: string;
  channel: string;
  value: string;
  channelContactId?: string;
  linkedAt?: string;
  isPrimary?: boolean;
};

const asArray = (value: any): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean).map(String) : [String(value)];
};

const normalizeIdentifier = (value?: string | null, channel?: string) => {
  const rawValue = value || '';
  const compact = rawValue.replace(/^\+/, '').replace(/[\s().-]/g, '').trim();
  const isPhoneLike = channel === 'phone' || channel === 'whatsapp';
  if (!isPhoneLike) return compact.toLowerCase();

  if (isMaskedPhoneValue(rawValue) || rawValue.includes('•')) {
    const visibleDigits = rawValue.replace(/\D/g, '');
    return visibleDigits.length > 4 ? visibleDigits.slice(-4) : visibleDigits;
  }

  const digitsOnly = rawValue.replace(/\D/g, '');
  if (!digitsOnly) return compact.toLowerCase();

  if (!rawValue.trim().startsWith('+') && digitsOnly.startsWith('0')) {
    return digitsOnly.replace(/^0+/, '');
  }

  const parsed = parseInternationalPhoneNumber(rawValue.trim().startsWith('+') ? rawValue : `+${digitsOnly}`);
  if (parsed?.nationalNumber) {
    return parsed.nationalNumber.replace(/\D/g, '');
  }

  const withoutEgyptCode = digitsOnly.startsWith('20') ? digitsOnly.slice(2) : digitsOnly;
  return withoutEgyptCode.replace(/^0+/, '');
};

export const getContactConversationIdentifierOptions = (contact?: Contact | null): ContactIdentifierOption[] => {
  if (!contact) return [];
  const parentContact = (contact as any).parentContact || {};
  const options: ContactIdentifierOption[] = [];
  const seen = new Set<string>();

  const addOption = (option: Omit<ContactIdentifierOption, 'key'>) => {
    if (!option.value) return;
    const channel = option.channel;
    if (!PANE_IDENTIFIER_CHANNELS.has(channel)) return;
    const normalized = normalizeIdentifier(option.value, channel);
    const key = `${channel}:${normalized}`;
    if (seen.has(key)) return;
    seen.add(key);
    options.push({ ...option, channel, key });
  };

  (parentContact.linkedChannelContacts || []).forEach((linked: any) => {
    addOption({
      channel: linked.channel,
      value: linked.channelIdentifier,
      channelContactId: linked.channelContactId,
      linkedAt: linked.linkedAt || linked.updatedAt || linked.createdAt,
      isPrimary: !!linked.isPrimary,
    });
  });

  const identifiers = parentContact.identifiers || {};
  if (Array.isArray(identifiers)) {
    identifiers.forEach((identifier: any) => {
      addOption({
        channel: identifier.type,
        value: identifier.value,
        linkedAt: identifier.updatedAt || identifier.createdAt,
        isPrimary: !!identifier.isPrimary,
      });
    });
  } else {
    Object.entries(identifiers).forEach(([channel, values]) => {
      asArray(values).forEach((value) => addOption({ channel, value }));
    });
  }

  if ((contact as any).phoneNumber || (contact as any).phone) {
    addOption({
      channel: (contact as any).channel || 'whatsapp',
      value: (contact as any).phoneNumber || (contact as any).phone,
    });
  }

  return options.sort((a, b) => {
    const linkedDiff = new Date(b.linkedAt || 0).getTime() - new Date(a.linkedAt || 0).getTime();
    if (linkedDiff !== 0) return linkedDiff;
    if (!!b.isPrimary !== !!a.isPrimary) return b.isPrimary ? 1 : -1;
    return 0;
  });
};

const resolveConversationIdentifierOption = (contact: Contact, channel?: string, identifierValue?: string): ContactIdentifierOption => {
  const options = getContactConversationIdentifierOptions(contact);
  // Nothing specific asked for (a plain profile open, not a click on a conversation or channel):
  // land on the channel last used with this contact.
  if (!identifierValue && !channel) {
    channel = getLastCommunicationChannel((contact as any).parentContact, options.map((option) => option.channel)) || undefined;
  }
  if (identifierValue) {
    const resolvedChannel = channel || (contact as any).channel || 'whatsapp';
    const normalizedValue = normalizeIdentifier(identifierValue, resolvedChannel);
    return options.find((option) => option.channel === resolvedChannel && normalizeIdentifier(option.value, option.channel) === normalizedValue)
      || { key: `${resolvedChannel}:${normalizedValue}`, channel: resolvedChannel, value: identifierValue };
  }
  if (channel) {
    const channelOption = options.find((option) => option.channel === channel);
    if (channelOption) return channelOption;
  }
  return options[0] || {
    key: `${(contact as any).channel || 'whatsapp'}:${normalizeIdentifier((contact as any).phoneNumber || (contact as any).phone || '', (contact as any).channel || 'whatsapp')}`,
    channel: (contact as any).channel || 'whatsapp',
    value: (contact as any).phoneNumber || (contact as any).phone || '',
  };
};

/** Adds/removes a `contact` query param on the current URL via
 * history.replaceState — deliberately NOT pushState: this must never create
 * a new browser-history entry (opening/closing a contact in the pane isn't
 * a "page" the back button should step through), and staying out of
 * AppNavigator's own pushState-based navigateTo path-building avoids a real
 * bug that approach would hit (comparing the pathname-only current URL
 * against a newly-built path that includes a query string is always
 * "different", so every call would push a fresh history entry). Web only —
 * a no-op on native, where there's no pane/URL concept to sync anyway. */
function syncContactInUrl(contactId: string | null) {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (contactId) {
    url.searchParams.set('contact', contactId);
  } else {
    url.searchParams.delete('contact');
  }
  const qs = url.searchParams.toString();
  window.history.replaceState(null, '', url.pathname + (qs ? `?${qs}` : '') + url.hash);
}

/** Resolves a contact to a conversation to open alongside its profile, shared
 * by every screen that lets a user click a contact in the persistent
 * contacts pane to view both at once (profile in the middle, conversation in
 * the right pane). Only the resolution itself is shared here — each screen
 * keeps its own existing middle/right-pane state shape, since those differ
 * per screen (e.g. ContactsListScreen already has its own profile-view
 * state; Home/Inbox need a new one).
 *
 * Also keeps a `?contact=<id>` query param on the URL in sync with
 * open/closed state (see syncContactInUrl's own doc comment), and exposes
 * openContactById to resolve + open a contact from just an id — used to
 * restore that state on page load/refresh. */
export function useContactConversationPane() {
  const [viewedContact, setViewedContact] = useState<Contact | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  // Which specific channel/identifier the open conversation was resolved
  // against — a contact can have more than one number for the same channel
  // (two WhatsApp numbers, say), so "the conversation" alone doesn't say
  // which one; the pane header displays this so it's never ambiguous.
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedIdentifierValue, setSelectedIdentifierValue] = useState<string | null>(null);
  // Phone identifiers don't open a conversation (a call isn't a chat thread)
  // — they show this instead, in the same pane slot. Mutually exclusive with
  // viewedContact/conversationId above: opening one closes the other.
  const [callHistoryTarget, setCallHistoryTarget] = useState<{ contactId: string; contactName: string; phoneNumber?: string; phoneIndex?: number } | null>(null);

  // `channel`, when passed, overrides contact.channel — used when the user
  // clicks a SPECIFIC channel badge in ContactEditForm's channels row
  // (a contact can have more than one linked channel; without this it was
  // impossible to open anything but whichever channel happened to be the
  // contact's primary one). `identifierValue`, when passed, is that exact
  // badge's raw value (ContactEditForm's channels row collapses multiple
  // identifiers of the same type into one icon with a picker, so which VALUE
  // was tapped isn't recoverable from contact.phoneNumber alone) — falls
  // back to contact.phoneNumber/phone when omitted (e.g. a plain row click
  // with no specific identifier in play). Re-calling this for an already-
  // open contact with a different channel/identifier is safe — it just
  // resets conversationId and re-resolves against the new one.
  const openContactProfile = (contact: Contact, channel?: string, identifierValue?: string) => {
    const selectedIdentifier = resolveConversationIdentifierOption(contact, channel, identifierValue);
    setCallHistoryTarget(null);
    setViewedContact(contact);
    setConversationId(null);
    setSelectedChannel(selectedIdentifier.channel);
    setSelectedIdentifierValue(selectedIdentifier.value || null);
    syncContactInUrl(contact.id);
  };

  const buildContactFromParent = (parentContact: any, channel?: string): Contact => {
    let phoneValue = parentContact.profile?.phone;
    let emailValue = parentContact.profile?.email;
    if (!phoneValue && parentContact.identifiers) {
      phoneValue = Array.isArray(parentContact.identifiers)
        ? parentContact.identifiers.find((id: any) => id.type === 'phone' || id.type === 'whatsapp')?.value
        : asArray(parentContact.identifiers.phone)[0] || asArray(parentContact.identifiers.whatsapp)[0];
    }
    if (!emailValue && parentContact.identifiers) {
      emailValue = Array.isArray(parentContact.identifiers)
        ? parentContact.identifiers.find((id: any) => id.type === 'email')?.value
        : asArray(parentContact.identifiers.email)[0];
    }
    return {
      id: parentContact._id || parentContact.id,
      name: parentContact.profile?.name || '',
      channel: (channel as any) || 'whatsapp',
      phoneNumber: phoneValue,
      email: emailValue,
      profilePictureUrl: parentContact.profile?.avatar,
      createdAt: parentContact.createdAt || new Date().toISOString(),
      updatedAt: parentContact.updatedAt || new Date().toISOString(),
      parentContact,
    } as Contact;
  };

  const openPhoneIdentifier = (contact: Contact, option: ContactIdentifierOption) => {
    const phoneOptions = getContactConversationIdentifierOptions(contact).filter((identifier) => identifier.channel === 'phone');
    const phoneIndex = phoneOptions.findIndex((identifier) => identifier.key === option.key);

    setViewedContact(contact);
    setConversationId(null);
    setLoadingConversation(false);
    setSelectedChannel('phone');
    setSelectedIdentifierValue(option.value || null);
    setCallHistoryTarget({
      contactId: contact.id,
      contactName: contact.name,
      phoneNumber: option.value || undefined,
      phoneIndex: phoneIndex >= 0 ? phoneIndex : undefined,
    });
    syncContactInUrl(contact.id);
  };

  const openContact = async (contact: Contact, channel?: string, identifierValue?: string, channelContactId?: string, preferredConversationId?: string) => {
    const selectedIdentifier = resolveConversationIdentifierOption(contact, channel, identifierValue);
    const resolvedChannel = selectedIdentifier.channel;
    const resolvedValue = selectedIdentifier.value;
    const resolvedChannelContactId = channelContactId || selectedIdentifier.channelContactId;

    if (resolvedChannel === 'phone') {
      openPhoneIdentifier(contact, selectedIdentifier);
      return;
    }

    openContactProfile(contact, resolvedChannel, resolvedValue);
    if (preferredConversationId) {
      setConversationId(preferredConversationId);
      setLoadingConversation(false);
      return;
    }
    setLoadingConversation(true);
    try {
      let conversationPhoneNumber = resolvedValue || contact.phoneNumber || contact.phone;
      if (resolvedChannel === 'whatsapp' && (resolvedChannelContactId || !conversationPhoneNumber || isMaskedPhoneValue(conversationPhoneNumber))) {
        const hadMaskedPhoneNumber = isMaskedPhoneValue(conversationPhoneNumber);
        const target = await apiService.getContactDialTarget(contact.id, 'whatsapp', resolvedChannelContactId);
        if (target.statusCode === 1000 && target.data?.phoneNumber) {
          conversationPhoneNumber = target.data.phoneNumber;
        } else if (hadMaskedPhoneNumber) {
          conversationPhoneNumber = undefined;
        }
      }
      // Real registered route is create-or-find, not findOrCreate (confirmed
      // against omni-channel.module.ts's own route table) — AppNavigator.tsx's
      // contact-notification handler has this exact same wrong-URL bug,
      // inherited from copying that code as the precedent; fixed there too.
      // contactName/phoneNumber are required by the backend (used to build a
      // brand-new conversation's payload, and phoneNumber is WhatsApp's
      // authoritative lookup key for an EXISTING one) — omitting them is why
      // this silently never resolved a conversationId before.
      const res = await apiService.authenticatedRequest<{ conversationId: string }>(
        '/api/omni-channel/conversations/create-or-find',
        {
          method: 'POST',
          body: JSON.stringify({
            contactId: contact.id,
            contactName: contact.name,
            phoneNumber: conversationPhoneNumber,
            channel: resolvedChannel,
          }),
        }
      );
      if (res.data?.conversationId) {
        setConversationId(res.data.conversationId);
      } else {
        console.error('[useContactConversationPane] create-or-find returned no conversationId for contact', contact.id, res.errors);
      }
    } catch (error) {
      console.error('[useContactConversationPane] Failed to resolve conversation for contact', contact.id, error);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Resolves a bare contact id into the same Contact shape a row click
  // already produces, then opens it the same way — used to restore a
  // `?contact=<id>` deep link on page load. Reuses the exact shape-building
  // ConversationScreen.tsx's own handleViewContact already does from this
  // same endpoint (ContactEditForm needs the raw omni-contact nested under
  // `.parentContact`, not just the top-level API response).
  const openContactById = async (contactId: string, channel?: string, options?: { identifierValue?: string; channelContactId?: string; conversationId?: string; profileOnly?: boolean }) => {
    try {
      const result = await apiService.getParentContactById(contactId);
      if (result.statusCode !== 1000 || !result.data) {
        console.error('[useContactConversationPane] Failed to resolve contact for deep link', contactId, result.errors);
        return null;
      }
      const parentContact = result.data;
      const contact = buildContactFromParent(parentContact, channel);
      if (options?.profileOnly) {
        openContactProfile(contact, channel, options.identifierValue);
      } else {
        await openContact(contact, channel, options?.identifierValue, options?.channelContactId, options?.conversationId);
      }
      return contact;
    } catch (error) {
      console.error('[useContactConversationPane] Error resolving deep-linked contact', contactId, error);
      return null;
    }
  };

  const closeContact = () => {
    setViewedContact(null);
    setConversationId(null);
    setCallHistoryTarget(null);
    setSelectedChannel(null);
    setSelectedIdentifierValue(null);
    syncContactInUrl(null);
  };

  // Opens Call History in the same right-pane slot a conversation would
  // occupy — only clears conversationId (so the right pane doesn't try to
  // show both), deliberately leaving viewedContact alone. Home/InboxScreen
  // key their middle-pane PROFILE view off viewedContact directly; clearing
  // it here would wrongly collapse the profile the user tapped phone FROM
  // back to the dashboard/conversation list the moment call history opens.
  const openCallHistory = (contactId: string, contactName: string, phoneNumber?: string, phoneIndex?: number) => {
    setConversationId(null);
    setSelectedChannel('phone');
    setSelectedIdentifierValue(phoneNumber || null);
    setCallHistoryTarget({ contactId, contactName, phoneNumber, phoneIndex });
  };

  const closeCallHistory = () => setCallHistoryTarget(null);
  const identifierOptions = getContactConversationIdentifierOptions(viewedContact);

  const switchConversationIdentifier = (option: ContactIdentifierOption) => {
    if (!viewedContact) return;
    if (option.channel === 'phone') {
      openPhoneIdentifier(viewedContact, option);
      return;
    }
    openContact(viewedContact, option.channel, option.value, option.channelContactId);
  };

  return {
    viewedContact,
    conversationId,
    loadingConversation,
    selectedChannel,
    selectedIdentifierValue,
    identifierOptions,
    switchConversationIdentifier,
    openContact,
    openContactProfile,
    openContactById,
    closeContact,
    callHistoryTarget,
    openCallHistory,
    closeCallHistory,
  };
}
